import {
  Configuration,
  Custom,
  DvmData,
  DvmStyles,
  LegalTextData,
  Theme,
  Tutorial,
} from '@core/models';
import { checkoutTutorialSteps } from '../tutorial/checkout/steps';
import {
  seatSelectionDesktopTutorialSteps,
  seatSelectionMobileTutorialSteps,
} from '../tutorial/seat-selection/steps';

const custom: Custom = {
  paymentPage: {
    captureAddress: false,
    capturePhone: true,
  },
};

const theme: Theme = {
  'default-color': '23 52 130',
  'main-color': '23 52 130',
  'secondary-color': '25 149 255',
  'tertiary-color': '242 246 250',
  'accent-color': '189 48 57',
  red: '189 48 57',
  success: '86 163 67',
  'success-off-white': '236 246 232',
};

const dvmData: DvmData = {
  venueId: 'nam-us-00099-chicagocubs',
  mapId: 'main_venuemap',
  miniMapId: 'main_venuemap_minimap',
  miniMapEnabled: true,
  gaSectorsIdArray: [],
  gaAdaIdArray: [],
  sectionOffsets: {
    '4352': [-200, -300],
  },
  limitedZoom: true,
};

const dvmStyles: DvmStyles = {
  'seat-available-none-fill': '25 149 255',
  'seat-available-none-stroke': '0 90 156',
  'seat-available-disabled-fill': '25 149 255',
  'seat-available-disabled-stroke': '0 90 156',
  'seat-unavailable-none': '214 214 214',
  'seat-available-pending': '230 145 56',
  'section-available-normal-fill': '25 149 255',
  'section-available-normal-stroke': '0 90 156',
  'section-available-normal-group1-fill': '255 77 77',
  'section-available-normal-group1-stroke': '255 77 77',
  'section-available-hover-fill': '0 90 156',
  'section-available-hover-stroke': '255 255 255',
  'section-selected-normal-fill': '225 56 63',
  'section-selected-normal-stroke': '225 56 63',
  'section-selected-hover-stroke': '255 255 255',
  'section-nh-available-normal-fill': '229 154 65',
  'section-nh-available-normal-stroke': '229 154 65',
  'section-nh-available-hover-fill': '229 154 65',
  'section-nh-available-hover-stroke': '229 154 65',
  'section-nh-unavailable-normal-fill': 'transparent',
  'section-nh-unavailable-normal-stroke': 'transparent',
  'section-nh-unavailable-hover-fill': 'transparent',
  'section-nh-unavailable-hover-stroke': 'transparent',
};

const seatSelectionDesktopTutorial: Tutorial = {
  sectionAvailabilityIds: ['4345', '4342', '4329'],
  availableSeatsQuantity: 400,
  selectedSeatsQuantity: 60,
  allowClose: false,
  doneBtnText: 'End Tutorial',
  nextBtnText: 'Next',
  popoverClass: 'driverjs-theme',
  prevBtnText: 'Back',
  steps: seatSelectionDesktopTutorialSteps,
};

const seatSelectionMobileTutorial: Tutorial = {
  ...seatSelectionDesktopTutorial,
  steps: seatSelectionMobileTutorialSteps,
};

const checkoutTutorial: Tutorial = {
  ...seatSelectionDesktopTutorial,
  steps: [
    checkoutTutorialSteps[0],
    {
      ...checkoutTutorialSteps[2],
      popover: {
        ...checkoutTutorialSteps[2].popover,
        description:
          '2. Review the details of your group ticket pricing. Click “Proceed to Payment” to complete your purchase.',
      },
    },
  ],
};

export const ChicagoCubsSpring: Configuration = {
  id: 'chicagocubsspring',
  clientName: 'Chicago Cubs Spring',
  appName: 'Chicago Cubs Spring',
  city: 'Chicago',
  favicon: '/assets/img/cubsspring/cubs_favicon.ico',
  logo: '/assets/img/cubsspring/cubs_logo.png',
  phone: '',
  email: 'mesa@cubs.com',
  paymentMethod: 'braintree',
  rainout: true,
  upsells: false,
  clientWebUrl: 'https://www.cubs.com/',
  clientWebText: 'www.cubs.com',
  clientBallParkUrl: 'https://www.mlb.com/cubs/apps/ballpark',
  clientBallParkText: 'cubs.com/BallparkApp',
  termsOfUseUrl: 'https://www.mlb.com/official-information/terms-of-use',
  mlbShopUrl:
    'https://www.mlbshopeurope.com/en/chicago-cubs/t-36228622+z-808474-1125435766',
  merchandiseWebUrl: '',
  rainoutInsuranceUrl: '',
  termsAndConditionsUrl:
    'https://www.mlb.com/cubs/tickets/terms-and-conditions',
  analytics: '',
  adas: true,
  theme,
  dvmData,
  dvmStyles,
  tutorial: {
    seatSelection: {
      desktop: seatSelectionDesktopTutorial,
      mobile: seatSelectionMobileTutorial,
    },
    checkout: { desktop: checkoutTutorial, mobile: checkoutTutorial },
  },
  cartSummaryText: 'Here you can see your new requested tickets',
  custom,
  extraCheckbox: {
    active: false,
    required: false,
    text: '',
  },
  summaryPage: {
    image1: '/assets/img/cubsspring/cubs_logo.png',
    image2: '/assets/img/cubsspring/suits-img.png',
  },
  get legalText(): LegalTextData {
    return {
      links: {
        termsLink: {
          href: this.termsAndConditionsUrl,
          text: 'Terms and Conditions',
          target: '_blank',
        },
        clubWebLink: {
          href: this.clientWebUrl,
          text: this.clientWebText,
          target: '_blank',
        },
      },
      text: [
        `As part of your ticket purchase, you are required to acknowledge the {termsLink}.
        By clicking the Place Order button, you agree to accept all {termsLink} applicable to ticket purchase, and all related policies.
        Further, due to the COVID-19 pandemic, scheduling may be impacted and games may be canceled,
        rescheduled, postponed or games may be held with limited capacity. Games may also have restricted capacity that prevents the use of specific
        seats. In the event that the game(s) for which the ticket(s) is issued does not occur or you are unable to use the
        specific seat(s) purchased, a refund or credit will be issued according to the refund policy available at {clubWebLink}.`,

        `By selecting the Place Order button you agree to purchase the tickets selected, to accept any restrictions noted, and
        understand you are responsible for any payment needed to complete the purchase. Upon completing your purchase by
        selecting the Place Order button, you acknowledge and agree your credit card will be charged. All sales are final. All
        tickets are subject to the {termsLink}.`,

        `In connection with your purchase, you may receive customer service-related emails from the ${this.clientName}, including
        optional surveys regarding your gameday experience. You also may receive ticket-related commercial email from the
        ${this.clientName}. You may unsubscribe from ticket-related commercial emails at any time.`,
      ],
    };
  },
  defaultTooltipText: 'Review seats before continuing',
  teamId: '112',
  metaPixelAnalytics: false,
};
